import axios, { AxiosHeaders, AxiosRequestConfig } from "axios";
import { apiAddress } from "./config";
import {
  ISearchParameters,
  ITableReport,
  Report,
} from "../Interfaces/TableInterfaces";
import { FileResponse } from "./portfolioRequests";
import { DropdownItem } from "../Interfaces/dropdownInterfaces";

axios.defaults.baseURL = apiAddress();
axios.defaults.headers.common["Authorization"] = localStorage.getItem("token");

const withAuthConfig = (
  axiosOptions?: AxiosRequestConfig,
): AxiosRequestConfig => {
  const headers = AxiosHeaders.from(axiosOptions?.headers as any);
  const token = localStorage.getItem("token");

  if (token) {
    headers.set("Authorization", token);
  } else {
    headers.delete("Authorization");
  }

  return {
    ...axiosOptions,
    headers,
  };
};

export const getRequest = async <Response>(
  url: string,
  axiosOptions?: AxiosRequestConfig,
): Promise<Response> => {
  const res = await axios.get(url, withAuthConfig(axiosOptions));
  return res.data;
};

export const postRequest = async <Body, Response>(
  url: string,
  body: Body,
  axiosOptions?: AxiosRequestConfig,
): Promise<Response> => {
  const res = await axios.post(url, body, withAuthConfig(axiosOptions));
  return res.data;
};

export const patchRequest = async <Body, Response>(
  url: string,
  body: Body,
  axiosOptions?: AxiosRequestConfig,
): Promise<Response> => {
  const res = await axios.patch(url, body, withAuthConfig(axiosOptions));
  return res.data;
};

export const putRequest = async <Body, Response>(
  url: string,
  body?: Body,
  axiosOptions?: AxiosRequestConfig,
): Promise<Response> => {
  const res = await axios.put(url, body, withAuthConfig(axiosOptions));
  return res.data;
};
export const deleteRequest = async <Body, Response>(
  url: string,
  body?: Body,
  axiosOptions?: AxiosRequestConfig,
): Promise<Response> => {
  const requestConfig = withAuthConfig(axiosOptions);

  if (typeof body === "undefined") {
    const res = await axios.delete(url, requestConfig);
    return res.data;
  }
  const res = await axios.delete(url, {
    ...requestConfig,
    data: body,
  });
  return res.data;
};

export const getTable = async <
  ISeachParam extends ISearchParameters | null,
  IReport extends ITableReport,
>(
  url: string,
  searchParam: ISeachParam | null,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IReport>> => {
  const urlSearchParam = new URLSearchParams();

  if (offset || offset === 0)
    urlSearchParam.append("Offset", offset.toString());
  if (pageSize || pageSize === 0)
    urlSearchParam.append("Pagesize", pageSize.toString());

  if (sortingType) urlSearchParam.append("sortingType", sortingType);
  if (additional)
    urlSearchParam.append(additional.name, additional.id.toString());

  const querySeparator = url.includes("?") ? "&" : "?";
  const res = await axios.post(
    `${url}${querySeparator}${urlSearchParam}`,
    searchParam,
    withAuthConfig(axiosOptions),
  );
  return res.data;
};

export const getTableWithGet = async <
  ISeachParam extends ISearchParameters | null,
  IReport extends ITableReport,
>(
  url: string,
  _: ISeachParam | null,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IReport>> => {
  const urlSearchParam = new URLSearchParams();

  if (offset || offset === 0)
    urlSearchParam.append("Offset", offset.toString());
  if (pageSize || pageSize === 0)
    urlSearchParam.append("Pagesize", pageSize.toString());

  if (sortingType) urlSearchParam.append("sortingType", sortingType);
  if (additional)
    urlSearchParam.append(additional.name, additional.id.toString());

  const res = await axios.get(
    `${url}?${urlSearchParam}`,
    withAuthConfig(axiosOptions),
  );
  return res.data;
};

export const getTableWithExtraData = async <
  TSeachParam extends ISearchParameters | null,
  TReport extends ITableReport,
  TResponse extends Report<TReport>,
>(
  url: string,
  searchParam: TSeachParam | null,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<TResponse> => {
  const urlSearchParam = new URLSearchParams();

  if (offset || offset === 0)
    urlSearchParam.append("Offset", offset.toString());
  if (pageSize || pageSize === 0)
    urlSearchParam.append("Pagesize", pageSize.toString());

  if (sortingType) urlSearchParam.append("sortingType", sortingType);
  if (additional)
    urlSearchParam.append(additional.name, additional.id.toString());

  const res = await axios.post(
    `${url}?${urlSearchParam}`,
    searchParam,
    withAuthConfig(axiosOptions),
  );
  return res.data;
};

export const downloadExcel = async (
  url: string,
  axiosOptions?: AxiosRequestConfig,
): Promise<FileResponse> => {
  const res = await axios(
    withAuthConfig({
      ...axiosOptions,
      url,
      method: "GET",
      responseType: "blob",
      baseURL: axiosOptions?.baseURL ?? apiAddress(),
    }),
  );

  console.log({ headers: res.headers });
  console.log({ res });

  let filename = "report.xlsx";
  if (res.headers["content-disposition"])
    filename = res.headers["content-disposition"]
      .split("filename=")[1]
      .split(";")[0]
      .slice(0, -1)
      .substring(1);
  return {
    blob: res.data,
    filename: filename,
  };
};

export const postDownloadExcel = async <T>(
  url: string,
  body: T,
  axiosOptions?: AxiosRequestConfig,
): Promise<FileResponse> => {
  const res = await axios(
    withAuthConfig({
      ...axiosOptions,
      url,
      method: "POST",
      responseType: "blob",
      data: body,
      baseURL: axiosOptions?.baseURL ?? apiAddress(),
    }),
  );
  const contentDisposition = res.headers["content-disposition"];
  let filename = "report.xlsx";
  console.log({ res });
  if (contentDisposition) {
    // Match both `filename=` and `filename*=UTF-8''` formats
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=(([\'"]).*?\2|[^;\n]*)/,
    );

    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, ""); // Remove quotes if present
    }
  }

  return {
    blob: res.data,
    filename: filename,
  };
};

export const getDownloadExcel = async <T>(
  url: string,
  axiosOptions?: AxiosRequestConfig,
): Promise<FileResponse> => {
  const res = await axios(
    withAuthConfig({
      ...axiosOptions,
      url,
      method: "GET",
      responseType: "blob",
      baseURL: axiosOptions?.baseURL ?? apiAddress(),
    }),
  );

  let filename = "report";

  try {
    filename = res.headers["content-disposition"]
      .split("filename=")[1]
      .split(";")[0];
    // .slice(0, -1)
    // .substring(1);
    console.log({
      filename: res.headers["content-disposition"]
        .split("filename=")[1]
        .split(";")[0],
    });
  } catch (e) {}

  return {
    blob: res.data,
    filename: filename,
  };
};
